import {Suspense, type PropsWithChildren} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {ForYouItemError, ForYouItemSkeleton} from './ForYouItemFallbacks'
import {useViewerRoles} from './use-viewer-roles'

export type ForYouAudience = 'author' | 'reviewer'

/**
 * Whether the current viewer "owns" the pull request — either as its author or as an assignee.
 *
 * Being an assignee covers scenarios like:
 *    - the PR is authored by a bot or other non-human account, and the viewer is the human responsible for it.
 *   - the PR is authored by a separate human, but the viewer is taking over responsibility for shipping it (e.g., the author is on vacation).
 *
 * Reads the overview route's assignees query, so this suspends.
 */
export function useViewerOwnsPullRequest(): boolean {
  const {isAuthor, isAssignee} = useViewerRoles()
  return isAuthor || isAssignee
}

/**
 * Error boundary + suspense wrapper for a single ForYou item. When `audience` is
 * provided, the item only renders for the matching viewer; otherwise it always
 * renders. The `author` audience includes assignees, so a `reviewer` is anyone who
 * does not own the pull request.
 */
export function ForYouItemBoundary({
  children,
  name,
  audience,
}: PropsWithChildren<{name: string; audience?: ForYouAudience}>) {
  const boundary = (
    <ErrorBoundary boundaryName={`ForYouItem.${name}`} fallback={<ForYouItemError />}>
      <Suspense fallback={<ForYouItemSkeleton />}>{children}</Suspense>
    </ErrorBoundary>
  )

  return audience ? <AudienceGate audience={audience}>{boundary}</AudienceGate> : boundary
}

/** Gates on whether the viewer "owns" the pull request (author or assignee) vs. is a reviewer. */
function AudienceGate({audience, children}: PropsWithChildren<{audience: ForYouAudience}>) {
  const viewerIsOwner = useViewerOwnsPullRequest()
  const matchesAudience = audience === 'author' ? viewerIsOwner : !viewerIsOwner

  return matchesAudience ? <>{children}</> : null
}
